// UGOT Pro 관리자 API 호출. admin-frontend 는 /api/v1/admin/apps 를 pay 로 넘긴다(vercel.json·vite proxy).
// 생성 클라이언트가 아직 이 경로를 모르므로 Q Language 관리 API 와 같은 방식으로 customInstance 를 직접 쓴다.
import axios from 'axios';
import {
  normalizeUgotProBilling,
  normalizeUgotProCancelResult,
  normalizeUgotProGrant,
  normalizeUgotProGrantList,
  normalizeUgotProRefundActionResult,
  normalizeUgotProRefundRequestList,
  type UgotProBilling,
  type UgotProCancelResult,
  type UgotProGrant,
  type UgotProGrantList,
  type UgotProRefundActionResult,
  type UgotProRefundRequest,
} from './ugotProTypes';

export const UGOT_PRO_ADMIN_BASE = '/api/v1/admin/apps/ugot-pro';

export type UgotProRequest = {
  method: 'GET' | 'PUT' | 'DELETE' | 'POST';
  url: string;
  params?: Record<string, string | number>;
  data?: Record<string, unknown>;
};

const userPath = (userId: string): string => {
  const trimmed = userId.trim();
  if (!trimmed) throw new Error('사용자 ID가 없습니다.');
  return `${UGOT_PRO_ADMIN_BASE}/users/${encodeURIComponent(trimmed)}`;
};

const requireReason = (value: string, label: string): string => {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`${label}를 입력해 주세요.`);
  if (trimmed.length > 500) throw new Error(`${label}는 500자 이하로 적어 주세요.`);
  return trimmed;
};

export const buildUgotProGrantListRequest = (endedLimit = 50): UgotProRequest => ({
  method: 'GET', url: `${UGOT_PRO_ADMIN_BASE}/grants`, params: { endedLimit },
});

/** expiresAt 은 UTC ISO(끝이 Z). 비우면 서버 기본(365일). */
export const buildUgotProGrantRequest = (
  userId: string,
  input: { note: string; expiresAt: string | null },
): UgotProRequest => ({
  method: 'PUT',
  url: `${userPath(userId)}/grant`,
  data: input.expiresAt
    ? { note: requireReason(input.note, '메모'), expiresAt: input.expiresAt }
    : { note: requireReason(input.note, '메모') },
});

export const buildUgotProRevokeRequest = (userId: string): UgotProRequest => ({
  method: 'DELETE', url: `${userPath(userId)}/grant`,
});

export const buildUgotProBillingRequest = (userId: string): UgotProRequest => ({
  method: 'GET', url: `${userPath(userId)}/billing`,
});

export const buildUgotProCancelRequest = (
  userId: string,
  subscriptionId: number,
  input: { immediate: boolean; reason: string },
): UgotProRequest => ({
  method: 'POST',
  url: `${userPath(userId)}/subscriptions/${subscriptionId}/cancel`,
  data: { immediate: input.immediate, reason: requireReason(input.reason, '해지 사유') },
});

export const buildUgotProRefundRequest = (userId: string, orderId: string, reason: string): UgotProRequest => ({
  method: 'POST',
  url: `${userPath(userId)}/orders/${encodeURIComponent(orderId)}/refund`,
  data: { reason: requireReason(reason, '환불 사유') },
});

export const buildUgotProRefundQueueRequest = (status: 'PENDING' | 'ALL' = 'PENDING'): UgotProRequest => ({
  method: 'GET', url: `${UGOT_PRO_ADMIN_BASE}/refund-requests`, params: { status },
});

/** amount 를 비우면(null) 전액 환불. 값은 통화 최소 단위(센트). */
export const buildUgotProApproveRequest = (
  refundRequestId: number,
  input: { amount: number | null; note: string },
): UgotProRequest => {
  const note = requireReason(input.note, '승인 메모');
  if (input.amount === null) {
    return { method: 'POST', url: `${UGOT_PRO_ADMIN_BASE}/refund-requests/${refundRequestId}/approve`, data: { note } };
  }
  if (!Number.isInteger(input.amount) || input.amount <= 0) {
    throw new Error('부분 환불 금액은 0보다 큰 정수(센트)여야 합니다.');
  }
  return {
    method: 'POST',
    url: `${UGOT_PRO_ADMIN_BASE}/refund-requests/${refundRequestId}/approve`,
    data: { amount: input.amount, note },
  };
};

export const buildUgotProRejectRequest = (refundRequestId: number, reason: string): UgotProRequest => ({
  method: 'POST',
  url: `${UGOT_PRO_ADMIN_BASE}/refund-requests/${refundRequestId}/reject`,
  data: { reason: requireReason(reason, '거절 사유') },
});

/** 서버가 준 오류 문구를 그대로 보여 준다(400·404·409). 문구가 없으면 HTTP 상태를 밝힌다. */
export const describeUgotProError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const body = error.response?.data as Record<string, unknown> | undefined;
    const message = body && typeof body['message'] === 'string' ? body['message'].trim() : '';
    if (status && message) return `${message} (HTTP ${status})`;
    if (status) return `요청이 거절되었습니다 (HTTP ${status}).`;
    return '결제 서버에 연결하지 못했습니다.';
  }
  return error instanceof Error && error.message ? error.message : '알 수 없는 오류가 발생했습니다.';
};

const send = async (request: UgotProRequest): Promise<unknown> => {
  const { customInstance } = await import('@edusense/payment-client/custom-axios');
  return customInstance<unknown>(request, { baseURL: '/' });
};

export const listUgotProGrants = async (): Promise<UgotProGrantList> => (
  normalizeUgotProGrantList(await send(buildUgotProGrantListRequest()))
);

export const grantUgotPro = async (
  userId: string,
  input: { note: string; expiresAt: string | null },
): Promise<UgotProGrant> => normalizeUgotProGrant(await send(buildUgotProGrantRequest(userId, input)));

export const revokeUgotPro = async (userId: string): Promise<UgotProGrant> => (
  normalizeUgotProGrant(await send(buildUgotProRevokeRequest(userId)))
);

export const getUgotProBilling = async (userId: string): Promise<UgotProBilling> => (
  normalizeUgotProBilling(await send(buildUgotProBillingRequest(userId)))
);

export const cancelUgotProSubscription = async (
  userId: string,
  subscriptionId: number,
  input: { immediate: boolean; reason: string },
): Promise<UgotProCancelResult> => (
  normalizeUgotProCancelResult(await send(buildUgotProCancelRequest(userId, subscriptionId, input)))
);

export const refundUgotProOrder = async (
  userId: string,
  orderId: string,
  reason: string,
): Promise<UgotProRefundActionResult> => (
  normalizeUgotProRefundActionResult(await send(buildUgotProRefundRequest(userId, orderId, reason)))
);

export const listUgotProRefundRequests = async (): Promise<UgotProRefundRequest[]> => (
  normalizeUgotProRefundRequestList(await send(buildUgotProRefundQueueRequest('PENDING')))
);

export const approveUgotProRefund = async (
  refundRequestId: number,
  input: { amount: number | null; note: string },
): Promise<UgotProRefundActionResult> => (
  normalizeUgotProRefundActionResult(await send(buildUgotProApproveRequest(refundRequestId, input)))
);

export const rejectUgotProRefund = async (
  refundRequestId: number,
  reason: string,
): Promise<UgotProRefundActionResult> => (
  normalizeUgotProRefundActionResult(await send(buildUgotProRejectRequest(refundRequestId, reason)))
);
